import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { CALENDLY_URL } from "@/lib/constants";

const steps = [
  {
    n: "01",
    title: "Kennenlerngespräch",
    desc: "Wir hören Dir zu und analysieren Deine aktuelle Situation sowie Deine Ziele.",
  },
  {
    n: "02",
    title: "Konzeptvorstellung",
    desc: "Du bekommst eine klare, individuelle Finanzstrategie – renditeorientiert, transparent und auf Dich zugeschnitten.",
  },
  {
    n: "03",
    title: "Umsetzung & Kontrolle",
    desc: "Wir setzen die erarbeitete Strategie mit Dir um und kontrollieren diese.",
  },
  {
    n: "04",
    title: "Langfristige Betreuung",
    desc: "Wir begleiten Dich auf Deiner finanziellen Reise und sprechen regelmäßig über unsere Strategie.",
  },
];

export default function Process() {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    if (!sectionRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            steps.forEach((_, i) => {
              setTimeout(() => setActiveIndex(i), i * 250);
            });
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  // line fill percentage based on active index (between first & last circle centers = 100%)
  const linePct =
    activeIndex < 0 ? 0 : Math.min(100, (activeIndex / (steps.length - 1)) * 100);

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-background">
      <div className="container">
        <h2 className="text-center font-display text-3xl md:text-4xl lg:text-5xl font-bold text-primary text-balance max-w-3xl mx-auto reveal">
          Dein Weg zu finanzieller Sicherheit
        </h2>

        <div className="mt-14 relative">
          {/* timeline line on desktop */}
          <div className="hidden lg:block absolute top-10 left-10 right-10 h-px bg-border overflow-hidden">
            <div
              className="h-full bg-gradient-accent transition-all duration-700 ease-out"
              style={{ width: `${linePct}%` }}
            />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {steps.map((s, i) => {
              const isActive = i <= activeIndex;
              return (
                <div
                  key={i}
                  className="relative transition-all duration-700 ease-out"
                  style={{
                    opacity: isActive ? 1 : 0,
                    transform: isActive ? "translateY(0)" : "translateY(24px)",
                  }}
                >
                  <div
                    className={`w-20 h-20 rounded-2xl flex items-center justify-center font-display text-2xl font-extrabold relative z-10 transition-all duration-500 ${
                      isActive
                        ? "bg-gradient-accent text-accent-foreground shadow-glow scale-100"
                        : "bg-muted text-muted-foreground scale-95"
                    }`}
                  >
                    {s.n}
                  </div>
                  <h3 className="mt-5 font-display text-xl font-bold text-primary">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-12 flex justify-center reveal">
          <Button asChild variant="accent" size="xl">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              Jetzt Erstgespräch vereinbaren <ArrowRight className="w-4 h-4" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}
